import React from 'react';
import _ from 'underscore';

import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FormInput from 'ui-library/bs/FormInput';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldError from 'ui-library/FieldError';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';
import {GenericEvent, InputChangeEvent, InputFocusEvent} from 'util/Types';

const MAX_NAME_LENGTH = 255;

interface Props {
  additionalSubmitDisabled: boolean;
  confirmButtonText: string;
  description: string;
  initialFolderId?: string;
  itemType: string;
  namePlaceholder: string;
  onConfirm: ({folderId, name}: State) => void;
  onRequestClose: () => void;
  title: string;
}

interface State {
  folderId?: string;
  name: string;
}

class CreateComponentModal extends React.Component<Props, State> {
  static open(props: Props): void {
    // eslint-disable-next-line no-restricted-syntax
    const modalProps = _.extend({}, props, {
      onRequestClose: () => {
        ModalContainer.close(CreateComponentModal);
      },
    });
    ModalContainer.open(CreateComponentModal, React.createElement(CreateComponentModal, modalProps));
  }

  static defaultProps = {
    additionalSubmitDisabled: false,
  };

  state: State = {
    folderId: this.props.initialFolderId,
    name: this.props.namePlaceholder,
  };

  _handleSelectFolder = (e: GenericEvent): void => {
    this.setState({
      folderId: e.target.value,
    });
  };

  _handleConfirm = (): void => {
    this.props.onConfirm(this.state);
    this.props.onRequestClose();
  };

  _handleChangeName = (e: InputChangeEvent): void => {
    this.setState({name: e.target.value});
  };

  _handleOnFocus = (e: InputFocusEvent): void => {
    const input = e.target;
    input.setSelectionRange(0, input.value.length);
  };

  render(): JSX.Element {
    const nameOrFolderMissing = !this.state.folderId || this.state.name.trim().length === 0;
    const nameIsTooLong = this.state.name.length > MAX_NAME_LENGTH;
    const errorMessage = nameIsTooLong
      ? `Names can be at most 255 characters long (currently ${this.state.name.length})`
      : undefined;
    return (
      <Modal
        title={this.props.title}
        onRequestClose={this.props.onRequestClose}
        buttons={[
          {
            text: this.props.confirmButtonText,
            onClick: this._handleConfirm,
            style: ButtonStyle.SECONDARY,
            disabled: nameOrFolderMissing || this.props.additionalSubmitDisabled || nameIsTooLong,
          },
        ]}
      >
        <div className="u-small-padding-bottom">{this.props.description}</div>

        <div className="form-group">
          <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
          <FolderSelector
            enableRedesign={true}
            showInventory={true}
            value={this.state.folderId}
            onChange={this._handleSelectFolder}
            unsetOnFetchFail={true}
            allowNone={false}
          />
        </div>

        <div className="form-group">
          <FieldLabel label={`Choose a name for the new ${this.props.itemType}:`} isRequired={true} />
          <FormInput
            focusOnMount={true}
            onFocus={this._handleOnFocus}
            placeholder={`For example: ${this.props.namePlaceholder}`}
            onChange={this._handleChangeName}
            type="text"
            value={this.state.name}
            dataTestAttrs={{component: 'CreateComponentModal', element: 'nameInput'}}
            error={errorMessage}
          />
          <FieldError message={errorMessage ?? null} />
        </div>
        {this.props.children}
      </Modal>
    );
  }
}

export default CreateComponentModal;
